import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { Injector } from "@/components/form/Injector";
import AntIcon from "@/components/icons/AntIcon";
import { AnonymousContentWrapper } from "@/components/wrappers/AnonymousContentWrapper";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { useCountdown } from "@/utils/hooksUtils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Button, Col, Form, Input, Row, Space, type InputRef } from "antd";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { useUserConfirmUser, useUserSendTotpCodeViaSms } from "../../../common/api/queries";
import { SMS_TOTP_CODE_RESEND_DELAY_SECONDS } from "../../../common/constants";
import { EntityIdObject } from "../../../common/types";
import { AUTH_ROUTE_PATHS } from "../../auth/paths";
import { DASHBOARD_ROUTE_PATHS } from "../../dashboard/paths";
import { ConfirmUser } from "../types";

export const ConfirmUserContainer = () => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const { id } = useParams<EntityIdObject>() as { id: string };
  const navigate = useNavigate();

  const [urlSearchParams] = useSearchParams();
  const token = urlSearchParams.get("token");
  const name = urlSearchParams.get("name");
  const phone = urlSearchParams.get("phone");
  const requirePassword = urlSearchParams.get("requirePassword") === "true";

  const [count, startCountdown] = useCountdown();
  const smsTotpCodeInputRef = useRef<InputRef>(null);

  const [form] = Form.useForm<ConfirmUser>();

  const confirmUser = useUserConfirmUser();
  const sendTotpCodeViaSms = useUserSendTotpCodeViaSms();

  if (!token || !name || !phone) {
    return <Navigate to={DASHBOARD_ROUTE_PATHS.homepage.to} replace />;
  }

  const handleFormFinish = (values: ConfirmUser) => {
    confirmUser.mutate(
      { id, object: { ...values, token } },
      {
        onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
        onSuccess: () => {
          modal.success({
            title: t("common.operationSuccess"),
            content: t("user.helpers.userConfirmationSuccess"),
            okText: t("user.actions.continueToLogin"),
            onOk: () => navigate(AUTH_ROUTE_PATHS.login.to, { replace: true }),
          });
        },
      },
    );
  };

  const handleSendTotpCodeViaSmsClick = () => {
    sendTotpCodeViaSms.mutate(
      { id, object: { token } },
      {
        onSuccess: () => {
          startCountdown(SMS_TOTP_CODE_RESEND_DELAY_SECONDS);
          smsTotpCodeInputRef.current?.focus();
        },
      },
    );
  };

  return (
    <AnonymousContentWrapper>
      <h2 className="center-align margin-bottom-large">{t("user.titles.confirm")}</h2>

      <Row justify="center">
        <Col style={{ flex: "0 0 400px" }}>
          <Form form={form} layout="vertical" name="confirmUserForm" onFinish={handleFormFinish}>
            {/* Name and phone are shown for confirmation only; the server takes them from the token. */}
            <Form.Item label={t("user.attrs.name")}>
              <Input value={name} disabled />
            </Form.Item>

            {requirePassword && (
              <>
                <Form.Item name="password" label={t("user.attrs.password")} rules={validations.sufficientPassword}>
                  <Input.Password prefix={<AntIcon type="lock" className="icon-black-opacity" />} />
                </Form.Item>

                <Form.Item
                  name="passwordRepeat"
                  label={t("user.attrs.passwordRepeat")}
                  rules={[...validations.sufficientPassword, validations.repeatedPassword()]}
                >
                  <Input.Password prefix={<AntIcon type="lock" className="icon-black-opacity" />} />
                </Form.Item>
              </>
            )}

            <Form.Item label={t("user.attrs.phone")}>
              <Input value={formatPhoneNumber(phone)} disabled />
            </Form.Item>

            <Form.Item
              name="smsTotpCode"
              label={t("user.attrs.smsTotpCode")}
              rules={[validations.notBlank, validations.length(6), validations.numeric]}
            >
              <Injector>
                {(props) => (
                  <Space.Compact style={{ width: "100%" }}>
                    <SecondaryButton
                      onClick={handleSendTotpCodeViaSmsClick}
                      icon={!count && <AntIcon type="send" />}
                      disabled={!!count}
                      loading={sendTotpCodeViaSms.isPending}
                    >
                      {!!count && `(${count}) `}
                      {t("user.actions.sendTotpCodeViaSms")}
                    </SecondaryButton>

                    <Input
                      ref={smsTotpCodeInputRef}
                      prefix={<AntIcon type="lock" className="icon-black-opacity" />}
                      {...props}
                    />
                  </Space.Compact>
                )}
              </Injector>
            </Form.Item>

            <Form.Item>
              <Button
                className="margin-top-medium"
                type="primary"
                htmlType="submit"
                block
                loading={confirmUser.isPending}
              >
                {t("user.actions.register")}
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </AnonymousContentWrapper>
  );
};
